/**
 * GH-945: why a card payment failed, and what the person reading can do about it.
 *
 * Stripe reports a failed card payment through up to three signals: the error `code`
 * (`card_declined`, `incorrect_cvc`, …), the issuer's `decline_code` (`insufficient_funds`, …) and,
 * on the charge, `outcome.reason` / `outcome.type`. `classifyCardFailure` folds them into one of a
 * small set of reasons, and each reason has one sentence for the donor paying online and one for the
 * staff member holding the reader (Tap to Pay), with the donor beside them. Every sentence names the
 * problem and, where there is one, the fix.
 *
 * Stripe asks that `fraudulent`, `lost_card`, `stolen_card` and `merchant_blacklist` be shown as an
 * ordinary decline (docs.stripe.com/declines/codes); `pickup_card`, `restricted_card`,
 * `lost_or_stolen_card` and a Radar block (`outcome.type = blocked`) are treated the same way here.
 * They classify as `generic_decline` and nothing downstream can tell them apart.
 *
 * `CardFailureCopy.swift` is the iOS twin of the copy and of `CARD_FAILURE_REASON_BY_CODE`, compared
 * character for character by `apps/card-failure-copy.test.ts`.
 */
import { z } from "zod";
export const CARD_FAILURE_REASONS = [
    "insufficient_funds",
    "card_limit",
    "expired_card",
    "incorrect_cvc",
    "incorrect_number",
    "incorrect_expiry",
    "incorrect_postal_code",
    "incorrect_address",
    "authentication_required",
    "authentication_failed",
    "pin_required",
    "incorrect_pin",
    "pin_tries_exceeded",
    "card_not_supported",
    "currency_not_supported",
    "amount_not_allowed",
    "too_many_attempts",
    "try_again",
    "contact_bank",
    "duplicate",
    "live_card_in_test",
    "test_card_in_live",
    "test_card_declined",
    "generic_decline",
];
export const cardFailureReasonSchema = z.enum(CARD_FAILURE_REASONS);
/**
 * Every Stripe code this table knows, whether it arrives as `decline_code`, error `code` or
 * `outcome.reason`. Anything not listed is a `generic_decline`.
 */
export const CARD_FAILURE_REASON_BY_CODE = {
    insufficient_funds: "insufficient_funds",
    card_velocity_exceeded: "card_limit",
    withdrawal_count_limit_exceeded: "card_limit",
    charge_exceeds_source_limit: "card_limit",
    charge_exceeds_transaction_limit: "card_limit",
    expired_card: "expired_card",
    expired_payment_method: "expired_card",
    incorrect_cvc: "incorrect_cvc",
    invalid_cvc: "incorrect_cvc",
    incorrect_number: "incorrect_number",
    invalid_number: "incorrect_number",
    invalid_expiry_month: "incorrect_expiry",
    invalid_expiry_year: "incorrect_expiry",
    incorrect_zip: "incorrect_postal_code",
    incorrect_postal_code: "incorrect_postal_code",
    postal_code_invalid: "incorrect_postal_code",
    incorrect_address: "incorrect_address",
    authentication_required: "authentication_required",
    authentication_not_handled: "authentication_required",
    mobile_device_authentication_required: "authentication_required",
    payment_intent_action_required: "authentication_required",
    authentication_failure: "authentication_failed",
    payment_intent_authentication_failure: "authentication_failed",
    setup_intent_authentication_failure: "authentication_failed",
    three_d_secure_authentication_not_supported: "authentication_failed",
    offline_pin_required: "pin_required",
    online_or_offline_pin_required: "pin_required",
    incorrect_pin: "incorrect_pin",
    invalid_pin: "incorrect_pin",
    pin_try_exceeded: "pin_tries_exceeded",
    card_not_supported: "card_not_supported",
    invalid_card_type: "card_not_supported",
    transaction_not_allowed: "card_not_supported",
    not_permitted: "card_not_supported",
    service_not_allowed: "card_not_supported",
    invalid_account: "card_not_supported",
    currency_not_supported: "currency_not_supported",
    payment_method_currency_mismatch: "currency_not_supported",
    amount_too_large: "amount_not_allowed",
    amount_too_small: "amount_not_allowed",
    invalid_amount: "amount_not_allowed",
    invalid_charge_amount: "amount_not_allowed",
    card_decline_rate_limit_exceeded: "too_many_attempts",
    processing_error: "try_again",
    issuer_not_available: "try_again",
    reenter_transaction: "try_again",
    try_again_later: "try_again",
    approve_with_id: "try_again",
    payment_method_provider_timeout: "try_again",
    call_issuer: "contact_bank",
    do_not_honor: "contact_bank",
    no_action_taken: "contact_bank",
    new_account_information_available: "contact_bank",
    revocation_of_all_authorizations: "contact_bank",
    revocation_of_authorization: "contact_bank",
    stop_payment_order: "contact_bank",
    security_violation: "contact_bank",
    duplicate_transaction: "duplicate",
    test_mode_live_card: "live_card_in_test",
    testmode_charges_only: "live_card_in_test",
    live_mode_test_card: "test_card_in_live",
    livemode_mismatch: "test_card_in_live",
    payment_method_invalid_parameter_testmode: "test_card_in_live",
    testmode_decline: "test_card_declined",
    // Stripe: never tell the payer these; show an ordinary decline.
    fraudulent: "generic_decline",
    lost_card: "generic_decline",
    stolen_card: "generic_decline",
    lost_or_stolen_card: "generic_decline",
    merchant_blacklist: "generic_decline",
    pickup_card: "generic_decline",
    restricted_card: "generic_decline",
    generic_decline: "generic_decline",
    card_declined: "generic_decline",
};
const known = (code) => code ? (CARD_FAILURE_REASON_BY_CODE[code] ?? null) : null;
/**
 * The most specific reason the signals support. A Radar block is always a generic decline; then
 * `outcome.reason`, `decline_code` and `code`, in that order, the first that says more than
 * "declined" winning.
 */
export function classifyCardFailure(signals) {
    if (signals.outcomeType === "blocked")
        return "generic_decline";
    const candidates = [signals.outcomeReason, signals.declineCode, signals.code].map(known);
    return (candidates.find((reason) => reason !== null && reason !== "generic_decline") ??
        "generic_decline");
}
/** The reason Stripe's stored code (one string) stands for — for a `failure_code` column. */
export function cardFailureReasonForCode(code) {
    return code ? (known(code) ?? "generic_decline") : null;
}
/** To the donor paying online. Says what happened and what to do; never blames the donor. */
export const CARD_FAILURE_DONOR_COPY = {
    insufficient_funds: "The card does not have enough available funds for this gift. Try a smaller amount or another card.",
    card_limit: "This gift is over the card's spending limit. Try another card, or ask your bank to raise the limit.",
    expired_card: "This card has expired. Use another card.",
    incorrect_cvc: "The security code does not match the card. Check the 3 or 4 digits on the card and try again.",
    incorrect_number: "The card number is not right. Check it and try again.",
    incorrect_expiry: "The expiration date is not right. Check it and try again.",
    incorrect_postal_code: "The ZIP or postal code does not match the card's billing address. Check it and try again.",
    incorrect_address: "The billing address does not match the card. Check it and try again, or use another card.",
    authentication_required: "Your bank needs you to confirm this gift. Try again and complete your bank's check when it appears.",
    authentication_failed: "Your bank's check did not go through, so nothing was charged. Try again, or use another card.",
    pin_required: "This card needs its PIN. Try again and enter the PIN when asked.",
    incorrect_pin: "The PIN was not right. Try again, or use another card.",
    pin_tries_exceeded: "The PIN was entered wrong too many times. Use another card; your bank can reset the PIN.",
    card_not_supported: "This card cannot be used for this kind of payment. Use another card or another way to give.",
    currency_not_supported: "This card does not accept payments in this currency. Use another card.",
    amount_not_allowed: "This amount cannot be charged to the card. Try a different amount, or another card.",
    too_many_attempts: "This card has been declined too many times in a row. Use another card, or try again tomorrow.",
    try_again: "The card's bank could not be reached just now, so nothing was charged. Wait a moment and try again.",
    contact_bank: "Your bank declined this gift. Call the number on the back of the card to allow it, or use another card.",
    duplicate: "A gift of the same amount on this card just went through. Check your email for its receipt before giving again.",
    live_card_in_test: "This is a test site, so real cards are always declined and nothing was charged. Use a Stripe test card such as 4242 4242 4242 4242.",
    test_card_in_live: "That is a test card number, which cannot make a real gift. Use a real card.",
    test_card_declined: "This test card is set up to be declined. Use another test card, such as 4242 4242 4242 4242.",
    generic_decline: "The card was declined. Use another card, or call the number on the back of the card.",
};
/**
 * To the staff member at the reader, with the donor beside them: what to say or do next.
 * Typing errors (number, security code, ZIP) cannot happen with a tapped card; their sentences
 * still read right if a reader ever reports one.
 */
export const CARD_FAILURE_STAFF_COPY = {
    insufficient_funds: "Not enough funds on this card. Ask for another card, or a smaller amount.",
    card_limit: "This gift is over the card's limit. Ask for another card, or a smaller amount.",
    expired_card: "This card has expired. Ask for another card.",
    incorrect_cvc: "The card's security code was not accepted. Ask for another card.",
    incorrect_number: "The card could not be read correctly. Tap again, or ask for another card.",
    incorrect_expiry: "The card could not be read correctly. Tap again, or ask for another card.",
    incorrect_postal_code: "The card's billing ZIP code did not match. Ask for another card.",
    incorrect_address: "The card's billing address did not match. Ask for another card.",
    authentication_required: "The bank wants the donor to confirm. Tap again; the donor enters their PIN or unlocks their phone if asked.",
    authentication_failed: "The bank's check did not go through. Nothing was charged. Tap again, or ask for another card.",
    pin_required: "This card needs its PIN. Tap again and let the donor enter it.",
    incorrect_pin: "The PIN was not right. Tap again and let the donor re-enter it, or ask for another card.",
    pin_tries_exceeded: "Too many wrong PIN attempts on this card. Ask for another card.",
    card_not_supported: "This card cannot be used here. Ask for another card, or another way to give.",
    currency_not_supported: "This card does not accept this currency. Ask for another card.",
    amount_not_allowed: "This amount cannot be charged to the card. Try a different amount, or another card.",
    too_many_attempts: "This card has been declined too many times in a row. Ask for another card.",
    try_again: "The card's bank did not answer. Nothing was charged. Tap again.",
    contact_bank: "The bank declined this card. Ask for another card; the donor can call their bank to allow it.",
    duplicate: "This matches a gift just made with this card. Check Gifts before trying again.",
    live_card_in_test: "This is a test setup, so real cards are always declined. Nothing was charged. Use a Stripe test card.",
    test_card_in_live: "That is a test card. Ask for a real card.",
    test_card_declined: "This test card is set up to be declined. Use another Stripe test card.",
    generic_decline: "The card was declined. Ask for another card, or another way to give.",
};
export function cardFailureMessage(reason, audience) {
    return audience === "donor" ? CARD_FAILURE_DONOR_COPY[reason] : CARD_FAILURE_STAFF_COPY[reason];
}
//# sourceMappingURL=card-failure.js.map